import { z } from 'zod'
import type { FollowUpPhaseDefinition } from '../constants/skill-check'
import { SettableNotificationChannelSchema } from './notification-channel'

const rangedInt = (min: number, max: number, message: string) =>
  z.number().int({ message }).min(min, { message }).max(max, { message })

export const FollowUpPhaseDefinitionSchema: z.ZodType<FollowUpPhaseDefinition> = z
  .object({
    label: z
      .string()
      .trim()
      .min(1, { message: '回の名称を入力してください。' })
      .max(50, { message: '回の名称は50文字以内で入力してください。' }),
    dueAfterMonths: rangedInt(0, 24, '期日の月数は0〜24で設定してください。'),
    dueAfterDays: rangedInt(0, 90, '期日の日数は0〜90で設定してください。'),
    openDaysBeforeDue: rangedInt(0, 90, '回答開始は期日の0〜90日前で設定してください。'),
  })
  .refine((phase) => phase.dueAfterMonths > 0 || phase.dueAfterDays > 0, {
    message: '期日は起点（研修日または前の回の期日）より後になるように設定してください。',
    path: ['dueAfterDays'],
  })

// 各回の期日は前の回の期日を起点にするため、回の順序は常に期日の昇順になる
export const FollowUpPhasesSchema = z
  .array(FollowUpPhaseDefinitionSchema)
  .min(1, { message: 'フォローアップは1回以上設定してください。' })
  .max(10, { message: 'フォローアップは10回以内で設定してください。' })

/**
 * Power Automate の HTTP/Teams Webhook トリガーのホスト。
 * LMS はこの URL にテナントのアプリ資格情報で取得したトークンを付けて送るため、他のホストは許可しない
 */
const POWER_AUTOMATE_WEBHOOK_HOST_SUFFIX = '.api.powerplatform.com'

export function isPowerAutomateWebhookUrl(value: string): boolean {
  try {
    const url = new URL(value)
    return url.protocol === 'https:' && url.hostname.endsWith(POWER_AUTOMATE_WEBHOOK_HOST_SUFFIX)
  } catch {
    return false
  }
}

/** Power Automate「Teams Webhook 要求を受信したとき」トリガーの URL。空文字は未設定として扱う */
export const TeamsWebhookUrlSchema = z
  .union([
    z.literal('').transform(() => null),
    z
      .string()
      .url({ message: '有効な URL を入力してください。' })
      .refine(isPowerAutomateWebhookUrl, {
        message:
          'Power Automate のトリガー URL（https://…api.powerplatform.com/…）を入力してください。',
      }),
  ])
  .nullable()

export const UpdateTenantTrainingConfigSchema = z.object({
  defaultNotificationChannel: SettableNotificationChannelSchema.optional(),
  defaultHrContactEmail: z
    .string()
    .email({ message: '有効なメールアドレスを入力してください。' })
    .optional()
    .nullable(),
  teamsWebhookUrl: TeamsWebhookUrlSchema.optional(),
  followUpPhases: FollowUpPhasesSchema.optional(),
  approvalRequiredForOpen: z.boolean().optional(),
  approvalTimeoutDays: z
    .number()
    .int()
    .min(1)
    .max(30, { message: '承認期限は1〜30日で設定してください。' })
    .optional(),
  reminderEnabled: z.boolean().optional(),
  reminderSendHour: z
    .number()
    .int()
    .min(0)
    .max(23, { message: 'リマインド送信時刻は0〜23で設定してください。' })
    .optional(),
})

export type UpdateTenantTrainingConfigInput = z.infer<typeof UpdateTenantTrainingConfigSchema>
